import { FeatureFlagIds } from "./featureFlagIds";
import {ApplicationBudget} from '../Data/ApplicationBudgetData';
import {IQuote, IQuoteBase} from '../Interfaces/Common/IQuote';
import Big from 'big.js';
import { AmortizationItem } from '../Interfaces/Common/AmortizationItem';
import { getApplicant } from './appUtils';
import { IQuotePayType } from "../Interfaces/Common/IQuotePayType";
import produce from "immer";

import { updateEditingApp } from "../store/application/actions";
import { ApplicationDispatch, SET_SERVER_MESSAGE } from "../store/application/types";
import { IApplication } from "../Interfaces/Application/IApplication";
import { BusinessRulesEngine } from "../Data/BusinessRulesData";
import { IRuleViolation } from "../Interfaces/Application/IApplicationQuote";
import { IState } from "../Interfaces/Common/IState";
import { fetchData } from "../Data/fetch";
import { IConfiguration } from '../Data/ConfigurationData';
import {
  ApplicationQuoteType, usesFinanceQuote,
  buildApplicationFinanceQuote,
  CarletonItem,
  divide,
  FinanceLoanClass, FinanceLoanClassFinanceFee, FinanceQuote, FinanceQuoteFee,
  FinanceQuoteFeeWithDescription,
  getNumberOfPaymentsForTermInMonthsAndNumberOfPaymentsPerYear, getNumberOfPaymentsPerYearForCarletonPaymentFrequency,
  getNumberOrZero, hasValue, multiply, PaymentFrequency,
  pow, ApplicationFinanceQuote, toNumber, Vehicle, ZipCode,
  updateFinanceLoanClassValuesOnFinanceQuote, updateFinanceQuoteZipCode,
  TradeInVehicle,
  buildDefaultFinanceQuote,
  convertContactOpportunityQuoteToApplicationFinanceQuote,
  getFinanceNumberOfPaymentsForTermInMonthsAndFrequency,
  FinanceWarranty,
  setDateTimeToNoon,
  addDays,
  FinanceQuoteNonUniformPaymentOption,
  SolveFor,
  getFinanceTermInMonthsForNumberOfPaymentsAndFrequency,
  updateSingleFinanceQuoteValue,
  toDollars
} from '@amsanalytics/react-shared-library';
import { IApplicationLeaseQuote } from '../Interfaces/Application/IApplicationLeaseQuote';
import { CSSProperties } from "react";
import { IAppCustomer } from '../Interfaces/Application/IAppCustomer';
import { Constants } from './constants';
import { isNotNil } from 'ramda';
import { ILeaseFee } from "../Interfaces/Common/ILeaseFee";
import { IEntityCustomField } from '../Interfaces/Application/IEntityCustomField';
import {Organization} from "@amsanalytics/react-shared-library/build/Models/Organization";
import {getAppQuoteByType} from "../Components/Quoters/IndirectQuoter/IndirectQuoterUtils";
import {IndirectQuoteType} from "../Data/IndirectQuoteType";
import {IEmployee} from "../Interfaces/Admin/IEmployee";
import {DealerMiscFee} from "../Data/IDealertrackDealerDecisionData";

export interface ApplicationIncomeAndDebtInfo {
  totalIncomePrimary: number;
  availableIncomePrimary: number;
  totalIncomeCobuyer1: number;
  availableIncomeCobuyer1: number;
  totalIncomeCobuyer2: number;
  availableIncomeCobuyer2: number;
  totalIncome: number;
  totalIncomeUsed: number;
  totalAvailableIncome: number; // total income used - total debt used
  totalDebtPrimary: number;
  availableDebtPrimary: number;
  totalDebtCobuyer1: number;
  availableDebtCobuyer1: number;
  totalDebtCobuyer2: number;
  availableDebtCobuyer2: number;
  totalDebt: number;
  totalDebtUsed: number;
  paymentToSubmit?: Big;
  totalGrossIncome: number;
  totalNetIncome: number;
}

// This answers "can this location be picked for this deal?" A location that lists no quote types is unrestricted and
// shows up for every deal. Once it lists some — Indirect only, say — it shows up only for Indirect deals. A deal
// with no quote type yet rules nothing out. The server applies the same "empty list means no restriction" rule to
// application queues in ApplicationQueueService.
export const organizationAllowsQuoteType = (
  organization: Organization | undefined | null,
  quoteType: ApplicationQuoteType | undefined | null
): boolean => {
  if (quoteType === undefined || quoteType === null) return true;

  const allowedQuoteTypes = organization?.applicationQuoteTypes;
  return !allowedQuoteTypes?.length || allowedQuoteTypes.includes(quoteType);
};

// True for an indirect deal whose client prints its loan documents inside the LOS. This combination is what the
// removed ApplicationQuoteType.IndirectWithInternalDocuments (4) used to express on its own: in-LOS document
// printing, the finance quoter "move forward" conversion flow, and the vehicle-required rule before approval.
export const usesInternalDocumentsForIndirect = (
  quoteType: ApplicationQuoteType | undefined | null,
  configuration?: IConfiguration
): boolean =>
  quoteType === ApplicationQuoteType.Indirect
  && configuration?.featureFlags?.[FeatureFlagIds.PrintDocumentsForIndirectDeals] === true;

// True when the 3-column indirect quoter is read-only: an internal-documents indirect deal whose status is in the
// tenant's InternalDocumentsIndirectQuoterReadOnlyStatuses config item (defaults to Ready to Book, Finalized & Posted
// and Desking). Status ids are compared case-insensitively because the API serializes Guids lowercase.
export const isInternalDocumentsIndirectQuoterReadOnly = (
  application?: IApplication | null,
  configuration?: IConfiguration
): boolean => {
  if (!application?.appStatusId || !usesInternalDocumentsForIndirect(application.quoteType, configuration)) return false;

  const appStatusId = application.appStatusId.toUpperCase();
  return !!configuration?.internalDocumentsIndirectQuoterReadOnlyStatuses?.some(id => id.toUpperCase() === appStatusId);
};

// True when such an application has been moved forward and should behave as finance-mode in the UI.
export const isIndirectConvertedToFinance = (
  application?: IApplication | null,
  configuration?: IConfiguration
): boolean => {
  if (!application) return false;

  return usesInternalDocumentsForIndirect(application.quoteType, configuration)
    && !!application.applicationQuotes?.some(
      aq => aq?.quote?.quoteType === IndirectQuoteType.Underwriter && hasValue(aq?.convertedToFinanceQuoteDate)
    )
    // Any deletedDate counts as deleted, like findSelectedFinanceQuote() and the finance quoter, so a deal this calls
    // converted always has a selected finance quote to validate.
    && !!application.financeQuotes?.some(fq => !hasValue(fq?.deletedDate));
};

// True when the deal posts to AMS from the selected finance quote: a finance-quoter deal (Finance, Refinance, Side
// Note), or an indirect deal that was moved forward. Mirrors the FinanceQuote branch of
// AmsLoanService.GetLoanPostingSource().
export const postsFromFinanceQuote = (
  application?: IApplication | null,
  configuration?: IConfiguration
): boolean =>
  usesFinanceQuote(application?.quoteType) || isIndirectConvertedToFinance(application, configuration);

// True when an indirect deal's numbers are re-run in the funding module, which is every indirect deal that posts
// from the underwriter quote. A client that prints its own indirect documents moves the deal forward to a finance
// quote at Dealer Review and posts from that instead, so it only falls back here for a deal that was never
// converted (it passed Dealer Review before the flag was turned on, or its converted finance quote was deleted).
export const usesFundingModule = (
  application?: IApplication | null,
  configuration?: IConfiguration
): boolean =>
  application?.quoteType === ApplicationQuoteType.Indirect && !isIndirectConvertedToFinance(application, configuration);

// The canonical indirect amount-financed formula. It backs the indirect quoter, the legacy dealer-view quoter, the
// use-vehicle-price reducer, the business-rules utilities and the C# twin (Quote.CalculateAmountToFinanceBasedOnCarPrice).
// Keep all of them in step.
export const getIndirectUnpaidBalance = (quote: IQuote): number => {
  return (quote.carSalePrice ?? 0)
    + (quote.taxes ?? 0)
    - (quote.tradeAllowance ?? 0)
    + (quote.tradeLien ?? 0)
    + (quote.docFee ?? 0)
    + (quote.taxTitleAndLicense ?? 0)
    - (quote.rebate ?? 0)
    - (quote.cashDown ?? 0)
    - (quote.deferredDown ?? 0);
};

// Unpaid balance plus the back-end products, then any client-configured misc fee adjustments. Callers without access
// to the client configuration (the reducer, business rules) omit the list, which matches their prior behavior.
export const calculateIndirectFinancedAmount = (
  quote: IQuote,
  miscFeeInformationList: DealerMiscFee[] = []
): number => {
  let amountFinanced = getIndirectUnpaidBalance(quote)
    + (quote.serviceContract ?? 0)
    + (quote.gapInsurance ?? 0)
    + (quote.creditLifeInsurance ?? 0)
    + (quote.disabilityInsurance ?? 0)
    + (quote.other ?? 0);

  // A configured fee key may be unset on the quote; anything that is not a finite number counts as zero so it
  // cannot poison the total
  miscFeeInformationList.forEach((mfi) => {
    const feeAmount = quote[mfi.key];
    const finiteFeeAmount = typeof feeAmount === 'number' && Number.isFinite(feeAmount) ? feeAmount : 0;
    if (mfi.calculation === 'add') {
      amountFinanced += finiteFeeAmount;
    } else if (mfi.calculation === 'subtract') {
      amountFinanced -= finiteFeeAmount;
    }
  });

  return amountFinanced;
};

// Everything in the amount financed other than the sale price and cash down. Display only; takes the same misc fee
// list as getAmountToFinance so the two agree.
export const getQuoteModifiers = (quote: IQuote, miscFeeInformationList: DealerMiscFee[] = []) => {
  return calculateIndirectFinancedAmount(quote, miscFeeInformationList) - (quote.carSalePrice ?? 0) + (quote.cashDown ?? 0);
}

// Keep the (quote, carPrice) shape: it is handed to tenant-authored business rules as a utility.
export const getAmountToFinance = (quote: IQuote, carPrice: number, miscFeeInformationList: DealerMiscFee[] = []) => {
  return calculateIndirectFinancedAmount({ ...quote, carSalePrice: carPrice }, miscFeeInformationList);
};

// The exact inverse of getAmountToFinance: backs Cash Down out of an amount financed the user typed in.
export const getCashDown = (quote: IQuote, miscFeeInformationList: DealerMiscFee[] = []): number => {
  const amountFinancedWithNoCashDown = calculateIndirectFinancedAmount({ ...quote, cashDown: 0 }, miscFeeInformationList);
  const cashDown = amountFinancedWithNoCashDown - (quote.amountToFinance ?? 0);
  return cashDown;
};

export const calculateLeasePayment = (apr: number, paymentsPerYear: number, term: number, amountToFinance: number): number => {
  return 0;
}

export const calculatePayment = (apr: number, paymentsPerYear: number, term: number, amountToFinance: number): number => {
  //
  //                    amountToFinance * (apr/paymentsPerYear)
  // payment  =  ----------------------------------------------------------
  //               1 - (apr/paymentsPerYear + 1)^((-term*paymentsPerYear)/12)
  if (paymentsPerYear === 0)
    return 0;

  const aprBig = Big(apr).div(100);
  const numerator = Big(amountToFinance).mul(aprBig.div(paymentsPerYear));
  const powVal = aprBig.div(paymentsPerYear).add(1);
  const power = Big(-term).mul(Big(paymentsPerYear).div(12));
  const denominator = Big(1).minus(Big(powVal).pow(Number.parseFloat(power.toFixed(0))));
  if (Number.parseFloat(denominator.toFixed(2)) === 0) return 0;

  // console.log('calculatePaymentParams', {
  //   apr,
  //   paymentsPerYear,
  //   term,
  //   amountToFinance
  // })
  return Number.parseFloat(numerator.div(denominator).toFixed(2));
};

/**
 * amountToFinance Calculation
 *                          (1 - (apr/paymentsPerYear + 1)^((-term*paymentsPerYear)/12)) * payment
 * amountToFinance  =   ------------------------------------------------------------------------------
 *                                             apr/paymentsPerYear
 *
 * @param incomeAndDebt
 * @param apr
 * @param paymentsPerYear
 * @param maxDebtToIncome
 * @param maxPaymentToIncome
 * @param minPercentCashDown
 * @param maxAmountToFinance
 * @param maxMonthlyPayment
 * @param financeTerm
 * @param downPayment
 * @param isAutomaticallyCalculated
 */
export const calculateMaxAmountToFinanceForTier = (
  incomeAndDebt: ApplicationIncomeAndDebtInfo,
  apr: number,
  paymentsPerYear: number,
  maxDebtToIncome: number,
  maxPaymentToIncome: number,
  minPercentCashDown: number,
  maxAmountToFinance: number,
  maxMonthlyPayment: number,
  financeTerm: number,
  downPayment?: number,
  isAutomaticallyCalculated?: boolean,
): { amountToFinance: number, payment: number, cashDown: number } | undefined => {
  // existingPayment?: number): {amountToFinance: number, payment: number, cashDown: number} | undefined => {

  let payment = 0;

  // let availableIncome = Big(incomeAndDebt.totalIncomeUsed).minus(Big(incomeAndDebt.totalDebtUsed));
  const paymentBasedOnMaxPaymentToIncome = Big(maxPaymentToIncome).mul(incomeAndDebt.totalIncomeUsed);

  if (isAutomaticallyCalculated) {
    let minPayment = Math.min(Number.parseFloat(paymentBasedOnMaxPaymentToIncome.toFixed(7)), maxMonthlyPayment);
    // Update the available income by adding the payment from the incomeAndDebtInfo in this case because that payment
    // is not the one calculated for the auto calculated quote.
    const debtWithoutPayment = Big(incomeAndDebt.totalDebtUsed).sub(incomeAndDebt.paymentToSubmit ?? 0);
    // availableIncome = Big(incomeAndDebt.totalIncomeUsed).minus(debtWithoutPayment);

    // const minPaymentForDTIWithoutUsingPaymentInDebt 
    // = Number.parseFloat(Big(maxDebtToIncome).mul(availableIncome).toFixed(7));

    //if(payment > minPaymentForDTIWithoutUsingPaymentInDebt) { // if less then it is should be good to go already
    // Get the absolute max payment while including DTI in the if payment is already less that

    // let availableIncomeWithPayment = Number.parseFloat(Big(incomeAndDebt.totalIncomeUsed).sub(minPayment).toFixed(7));      
    let debt = debtWithoutPayment.add(minPayment);
    let dti = incomeAndDebt.totalIncomeUsed === 0 ? 0 : Number.parseFloat(debt.div(incomeAndDebt.totalIncomeUsed).toFixed(7));

    while (dti > maxDebtToIncome && minPayment >= 0) {
      // Reduce the minPayment by 1 and keep checking until the minpayment is 0
      minPayment = Number.parseFloat(Big(minPayment).sub(1).toFixed(7));
      debt = debtWithoutPayment.add(minPayment);
      // Prevent divide by 0
      if (Number.parseFloat(incomeAndDebt.totalIncomeUsed.toFixed(7)) <= 0) {
        payment = 0;
      } else {
        dti = Number.parseFloat(debt.div(incomeAndDebt.totalIncomeUsed).toFixed(7));
      }
    }
    if (minPayment <= 0) {
      payment = 0;
    } else {
      payment = minPayment;
    }
    //}
  } else {
    const paymentBasedOnMaxDebtToIncome = Big(maxDebtToIncome).mul(incomeAndDebt.totalIncomeUsed); // .minus(existingPayment ? existingPayment: 0));
    payment = Math.min(Number.parseFloat(paymentBasedOnMaxPaymentToIncome.toFixed(7)), Number.parseFloat(paymentBasedOnMaxDebtToIncome.toFixed(7)), maxMonthlyPayment);
  }

  if (payment <= 0) payment = 0;

  const aprBig = Big(apr); // In this case apr is already a decimal value so no need to divide by 100
  const powVal = aprBig.div(paymentsPerYear).add(1);
  const power = Big(-financeTerm).mul(Big(paymentsPerYear).div(12));
  const numerator = (Big(1).minus(Big(powVal).pow(Number.parseFloat(power.toFixed(0))))).mul(payment);
  const denominator = aprBig.div(paymentsPerYear);
  const amountToFinanceBig = Number.parseFloat(denominator.toFixed(7)) === 0 ? 0 : numerator.div(denominator);

  // Because of slight differences in calculating the amount to finance by using a calculated max payment,
  // need to recalculate the payment based on the amount to finance.
  let amountToFinance = Math.min(Number.parseFloat(amountToFinanceBig.toFixed(2)), maxAmountToFinance);
  if (downPayment === 0 || downPayment === undefined || downPayment === null || Number.isNaN(downPayment) || isAutomaticallyCalculated === true) {
    downPayment = Number.parseFloat(Big(amountToFinance).mul(minPercentCashDown).toFixed(2));
  }

  if (downPayment && downPayment !== 0) {
    const maxAmountToFinanceFromDownPayment = Big(downPayment).div(minPercentCashDown);
    amountToFinance = Math.min(amountToFinance, Number.parseFloat(maxAmountToFinanceFromDownPayment.toFixed(2)));
  }
  const updatedPayment = calculatePayment(apr * 100, paymentsPerYear, financeTerm, amountToFinance);
  return { amountToFinance: amountToFinance, payment: updatedPayment, cashDown: downPayment };
}

export const getBig = (num: number): Big => {
  return Big(num);
};

export const getIncomeAndDebtBreakdown = (budget: ApplicationBudget, submittedIncome?: number): ApplicationIncomeAndDebtInfo => {

  const totalIncomePrimary = budget.incomeRows.reduce((total, x) => total.add(Big(x.primaryValue || '0')), Big(0));
  const totalIncomeCobuyer1 = budget.incomeRows.reduce((total, x) => total.add(Big(x.cobuyer1Value || '0')), Big(0));
  const totalIncomeCobuyer2 = budget.incomeRows.reduce((total, x) => total.add(Big(x.cobuyer2Value || '0')), Big(0));

  const netIncomePrimary = new Big(budget.includePrimaryApplicantIncome
    ? getNumberOrZero(budget.incomeRows?.find(ir => (ir.name === "Income" || ir.name === "Net Income") && ir.type === "Income")?.primaryValue) ?? 0
    : 0);
  const netIncomeCobuyer1 = new Big(budget.includeCoApplicant1Income
    ? getNumberOrZero(budget.incomeRows?.find(ir => (ir.name === "Income" || ir.name === "Net Income") && ir.type === "Income")?.cobuyer1Value) ?? 0
    : 0);
  const netIncomeCobuyer2 = new Big(budget.includeCoApplicant2Income
    ? getNumberOrZero(budget.incomeRows?.find(ir => (ir.name === "Income" || ir.name === "Net Income") && ir.type === "Income")?.cobuyer2Value) ?? 0
    : 0);

  const totalNetIncome = (budget.includePrimaryApplicantIncome ? netIncomePrimary : new Big(0))
    .add(budget.includeCoApplicant1Income ? netIncomeCobuyer1 : new Big(0))
    .add(budget.includeCoApplicant2Income ? netIncomeCobuyer2 : new Big(0));

  const totalIncomeUsed =
    (budget.includePrimaryApplicantIncome ? totalIncomePrimary : Big(0))
      .add(budget.includeCoApplicant1Income ? totalIncomeCobuyer1 : Big(0))
      .add(budget.includeCoApplicant2Income ? totalIncomeCobuyer2 : Big(0));




  // const grossIncome = totalIncomeUsed.sub((netIncomePrimary).add(netIncomeCobuyer1).add(netIncomeCobuyer2)).add(new Big(submittedIncome ?? 0));
  const totalDebtPrimary = budget.debtRows?.reduce((total, x) => total.add(Big(x.primaryValue || '0')), Big(0));
  const totalDebtCobuyer1 = budget.debtRows?.reduce((total, x) => total.add(Big(x.cobuyer1Value || '0')), Big(0));
  const totalDebtCobuyer2 = budget.debtRows?.reduce((total, x) => total.add(Big(x.cobuyer2Value || '0')), Big(0));

  const totalDebtUsed =
    (budget.includePrimaryApplicantIncome ? totalDebtPrimary : Big(0))
      .add(budget.includeCoApplicant1Income ? totalDebtCobuyer1 : Big(0))
      .add(budget.includeCoApplicant2Income ? totalDebtCobuyer2 : Big(0));

  const totalAvailableIncome = totalIncomeUsed.sub(totalDebtUsed);

  return {
    totalIncomePrimary: toNumber(totalIncomePrimary),
    availableIncomePrimary: budget.includePrimaryApplicantIncome ? toNumber(totalIncomePrimary) : 0,
    totalIncomeCobuyer1: toNumber(totalIncomeCobuyer1),
    availableIncomeCobuyer1: budget.includeCoApplicant1Income ? toNumber(totalIncomeCobuyer1) : 0,
    totalIncomeCobuyer2: toNumber(totalIncomeCobuyer2),
    availableIncomeCobuyer2: budget.includeCoApplicant2Income ? toNumber(totalIncomeCobuyer2) : 0,
    totalIncome: toNumber(totalIncomePrimary.add(totalIncomeCobuyer1).add(totalIncomeCobuyer2)),
    totalIncomeUsed: toNumber(totalIncomeUsed),
    totalAvailableIncome: toNumber(totalAvailableIncome),
    totalDebtPrimary: toNumber(totalDebtPrimary),
    availableDebtPrimary: budget.includePrimaryApplicantIncome ? toNumber(totalDebtPrimary) : 0,
    totalDebtCobuyer1: toNumber(totalDebtCobuyer1),
    availableDebtCobuyer1: budget.includeCoApplicant1Income ? toNumber(totalDebtCobuyer1) : 0,
    totalDebtCobuyer2: toNumber(totalDebtCobuyer2),
    availableDebtCobuyer2: budget.includeCoApplicant2Income ? toNumber(totalDebtCobuyer2) : 0,
    totalDebt: toNumber(totalDebtPrimary.add(totalDebtCobuyer1).add(totalDebtCobuyer2)),
    totalDebtUsed: toNumber(totalDebtUsed),
    paymentToSubmit: budget.debtRows.find(dr => dr.name === 'Payment')?.primaryValue ? Big(budget.debtRows.find(dr => dr.name === 'Payment')!.primaryValue!) : Big(0),
    totalGrossIncome: submittedIncome ?? 0,
    totalNetIncome: toNumber(totalNetIncome)
  };
};

// Calculates the income for the given applicant. Depends on:
// 1. whether or not the includeAllCurrentIncomes is checked
// 2. whether or not the "Additional Income" toggle switch is checked
const calculateApplicantIncomeInternal = (budget: ApplicationBudget, customerRoleId: string, includeAllCurrentIncomes?: boolean, includeAdditionalIncomes?: boolean): Big => {
  // Get primary income from budget
  let totalIncomePrimary: number = budget.incomeRows.reduce((total, x) => total + Number(customerRoleId.toUpperCase() === Constants.roleApplicantId.toUpperCase() ? x.primaryValue : customerRoleId.toUpperCase() === Constants.roleCoApplicant1Id.toUpperCase() ? x.cobuyer1Value : x.cobuyer2Value), 0);
  // Return the total income as Big
  return Big(totalIncomePrimary);
};

// Sum the incomes for the given app customers. Depends on:
// 1. whether or not the includeAllCurrentIncomes is checked
// 2. whether or not the "Additional Income" toggle switch is checked
const calculateNetIncomeForApplicantInternal = (budget: ApplicationBudget, appCustomer: IAppCustomer, includeAllCurrentIncomes?: boolean): Big => {
  let netIncome: number = 0;
  switch (appCustomer?.customerRoleId) {
    case Constants.roleApplicantId:
      netIncome = (getNumberOrZero(budget.incomeRows?.find(ir => ir.name === "Income" && ir.type === "Income")?.primaryValue) ?? 0) + (getNumberOrZero(budget.incomeRows?.find(ir => ir.name === "Additional Income" && ir.type === "Income")?.primaryValue) ?? 0);
      break
    case Constants.roleCoApplicant1Id:
      netIncome = (getNumberOrZero(budget.incomeRows?.find(ir => ir.name === "Income" && ir.type === "Income")?.cobuyer1Value) ?? 0) + (getNumberOrZero(budget.incomeRows?.find(ir => ir.name === "Additional Income" && ir.type === "Income")?.cobuyer1Value) ?? 0);
      break;
    case Constants.roleCoApplicant2Id:
      netIncome = (getNumberOrZero(budget.incomeRows?.find(ir => ir.name === "Income" && ir.type === "Income")?.cobuyer2Value) ?? 0) + (getNumberOrZero(budget.incomeRows?.find(ir => ir.name === "Additional Income" && ir.type === "Income")?.cobuyer2Value) ?? 0);
      break;
    default:
      netIncome = 0;
  }
  // Add in current incomes if includeAllCurrentIncomes is checked
  if (includeAllCurrentIncomes) {
    netIncome += appCustomer?.customer?.customerEmployment?.filter(i => i.isCurrent && i.primaryEmployment !== true)?.map(i => i.income)?.reduce((a, b) => a + b, 0) ?? 0;
  }
  // Return the total income as Big so we can apply math functions to it.
  return Big(netIncome);
};

// Returns object with income and debt info for all applicants. Depends on:
// 1. whether the includeAllCurrentIncomes is checked
// 2. whether the "Additional Income" toggle switch is checked
export const getIncomeAndDebtBreakdownInternal = (budget: ApplicationBudget, appCustomers: IAppCustomer[], submittedIncome?: number, includeAllCurrentIncomes?: boolean, includeAdditionalIncomes?: boolean): ApplicationIncomeAndDebtInfo => {
  const totalIncomePrimary = isNotNil(appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId)?.customer) ? calculateApplicantIncomeInternal(budget, Constants.roleApplicantId, includeAllCurrentIncomes, includeAdditionalIncomes) : Big(0);
  const totalIncomeCobuyer1 = isNotNil(appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleCoApplicant1Id)?.customer) ? calculateApplicantIncomeInternal(budget, Constants.roleCoApplicant1Id, includeAllCurrentIncomes, includeAdditionalIncomes) : Big(0);
  const totalIncomeCobuyer2 = isNotNil(appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleCoApplicant2Id)?.customer) ? calculateApplicantIncomeInternal(budget, Constants.roleCoApplicant2Id, includeAllCurrentIncomes, includeAdditionalIncomes) : Big(0);

  const netIncomePrimary = new Big(budget.includePrimaryApplicantIncome
    ? calculateNetIncomeForApplicantInternal(budget, appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId)!, includeAllCurrentIncomes)
    : 0);
  const netIncomeCobuyer1 = new Big(budget.includeCoApplicant1Income
    ? calculateNetIncomeForApplicantInternal(budget, appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleCoApplicant1Id)!, includeAllCurrentIncomes)
    : 0);
  const netIncomeCobuyer2 = new Big(budget.includeCoApplicant2Income
    ? calculateNetIncomeForApplicantInternal(budget, appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleCoApplicant2Id)!, includeAllCurrentIncomes)
    : 0);

  const totalNetIncome = (budget.includePrimaryApplicantIncome ? netIncomePrimary : new Big(0))
    .add(budget.includeCoApplicant1Income ? netIncomeCobuyer1 : new Big(0))
    .add(budget.includeCoApplicant2Income ? netIncomeCobuyer2 : new Big(0));

  const totalIncomeUsed =
    (budget.includePrimaryApplicantIncome ? totalIncomePrimary : Big(0))
      .add(budget.includeCoApplicant1Income ? totalIncomeCobuyer1 : Big(0))
      .add(budget.includeCoApplicant2Income ? totalIncomeCobuyer2 : Big(0));

  const totalDebtPrimary = budget.debtRows?.reduce((total, x) => total.add(Big(x.primaryValue || '0')), Big(0));
  const totalDebtCobuyer1 = budget.debtRows?.reduce((total, x) => total.add(Big(x.cobuyer1Value || '0')), Big(0));
  const totalDebtCobuyer2 = budget.debtRows?.reduce((total, x) => total.add(Big(x.cobuyer2Value || '0')), Big(0));

  const totalDebtUsed =
    (budget.includePrimaryApplicantIncome ? totalDebtPrimary : Big(0))
      .add(budget.includeCoApplicant1Income ? totalDebtCobuyer1 : Big(0))
      .add(budget.includeCoApplicant2Income ? totalDebtCobuyer2 : Big(0));

  const totalAvailableIncome = totalIncomeUsed.sub(totalDebtUsed);

  return {
    totalIncomePrimary: toNumber(totalIncomePrimary),
    availableIncomePrimary: budget.includePrimaryApplicantIncome ? toNumber(totalIncomePrimary) : 0,
    totalIncomeCobuyer1: toNumber(totalIncomeCobuyer1),
    availableIncomeCobuyer1: budget.includeCoApplicant1Income ? toNumber(totalIncomeCobuyer1) : 0,
    totalIncomeCobuyer2: toNumber(totalIncomeCobuyer2),
    availableIncomeCobuyer2: budget.includeCoApplicant2Income ? toNumber(totalIncomeCobuyer2) : 0,
    totalIncome: toNumber(totalIncomePrimary.add(totalIncomeCobuyer1).add(totalIncomeCobuyer2)),
    totalIncomeUsed: toNumber(totalIncomeUsed),
    totalAvailableIncome: toNumber(totalAvailableIncome),
    totalDebtPrimary: toNumber(totalDebtPrimary),
    availableDebtPrimary: budget.includePrimaryApplicantIncome ? toNumber(totalDebtPrimary) : 0,
    totalDebtCobuyer1: toNumber(totalDebtCobuyer1),
    availableDebtCobuyer1: budget.includeCoApplicant1Income ? toNumber(totalDebtCobuyer1) : 0,
    totalDebtCobuyer2: toNumber(totalDebtCobuyer2),
    availableDebtCobuyer2: budget.includeCoApplicant2Income ? toNumber(totalDebtCobuyer2) : 0,
    totalDebt: toNumber(totalDebtPrimary.add(totalDebtCobuyer1).add(totalDebtCobuyer2)),
    totalDebtUsed: toNumber(totalDebtUsed),
    paymentToSubmit: budget.debtRows.find(dr => dr.name === 'Payment')?.primaryValue ? Big(budget.debtRows.find(dr => dr.name === 'Payment')!.primaryValue!) : Big(0),
    totalGrossIncome: submittedIncome ?? 0,
    totalNetIncome: toNumber(totalNetIncome)
  };
};

export const calculateAmountToFinanceFromPayment = (
  payment: number | undefined, 
  quote: IQuote, 
  configuration: IConfiguration, 
  quotePayTypes: IQuotePayType[]
): number | null => {

  if (configuration?.featureFlags?.[FeatureFlagIds.ShowBiWeeklyTerm] === true) {
    return calculateAmountToFinanceFromPaymentRDF(payment, quote)
  }

  // This still be the default, to use the quote data. RDF's was specific for them don't want to edit that one at this time
  return calculateAmountToFinanceFromPaymentFromQuote(payment, quote, quotePayTypes);
};

export const calculateAmountToFinanceFromPaymentRDF = (payment: number | undefined, quote: IQuote): number | null => {
  // Reverse calculate the Amount that can be financed
  /**
   * amountToFinance Calculation
   *                          (1 - ((1 + apr/paymentsPerYear)^(-loanTerm*paymentsPerYear))) * payment   
   * amountToFinance  =   ------------------------------------------------------------------------------
   *                                             apr/paymentsPerYear
   */

  const apr = divide(quote.apr ?? 0, 100); // Decimal form
  const paymentsPerYear = 26; // RDF only does bi-weekly payments
  const loanTermInYears = 6;
  const maxAmountAvailable = 40000;

  const amountToFinanceTopLeft = (divide(apr, paymentsPerYear) + 1);
  const amountToFinanceTopRight = Math.round(divide(multiply(-loanTermInYears, paymentsPerYear), 1));
  const amountToFinanceTopCombined = 1 - pow(amountToFinanceTopLeft, amountToFinanceTopRight);

  const biweeklyPayment = payment ?? 0;
  const amountToFinanceTop = multiply(amountToFinanceTopCombined, biweeklyPayment);
  const amountToFinanceBottom = divide(apr, paymentsPerYear);
  let amountToFinance = divide(amountToFinanceTop, amountToFinanceBottom);
  amountToFinance = amountToFinance < maxAmountAvailable ? amountToFinance : maxAmountAvailable;

  return amountToFinance;
};

export const calculateAmountToFinanceFromPaymentFromQuote = (payment: number | undefined, quote: IQuote, quotePayTypes: IQuotePayType[]): number | null => {
  // Reverse calculate the Amount that can be financed
  /**
   * amountToFinance Calculation
   *                          (1 - ((1 + apr/paymentsPerYear)^(-loanTerm*paymentsPerYear))) * payment   
   * amountToFinance  =   ------------------------------------------------------------------------------
   *                                             apr/paymentsPerYear
   */
  
  const apr = divide(quote.apr ?? 0, 100); // Decimal form
  const paymentsPerYear = quotePayTypes.find(qpt => qpt.id === quote.quotePayTypeId)?.paymentsPerYear ?? 0;
  const loanTermInYears = hasValue(quote.term) ? divide(quote.term, 12) ?? 7 : 1;

  const amountToFinanceTopLeft = (divide(apr, paymentsPerYear) + 1);
  const amountToFinanceTopRight = Math.round(divide(multiply(-loanTermInYears, paymentsPerYear), 1));
  const amountToFinanceTopCombined = 1 - pow(amountToFinanceTopLeft, amountToFinanceTopRight);

  const amountToFinanceTop = multiply(amountToFinanceTopCombined, payment ?? 0);
  const amountToFinanceBottom = divide(apr, paymentsPerYear);
  let amountToFinance = divide(amountToFinanceTop, amountToFinanceBottom);

  return amountToFinance;
};

export const calculateLTV = (quote: IQuoteBase, vehicle?: Vehicle | null, displayAsPercentage?: boolean): number | string | null => {
  const ltv = quote.amountToFinance && vehicle?.bookoutValue
    ? Number.parseFloat((Big(quote.amountToFinance).div(vehicle.bookoutValue)).mul(100).toFixed(2))
    : quote.amountToFinance && vehicle?.nadaValue
      ? Number.parseFloat((Big(quote.amountToFinance).div(vehicle.nadaValue)).mul(100).toFixed(2))
      : quote.amountToFinance && quote.nadavalue
        ? Number.parseFloat((Big(quote.amountToFinance).div(quote.nadavalue)).mul(100).toFixed(2))
        : null;

  if (displayAsPercentage && ltv) {
    return `${ltv.toFixed(2)}%`;
  }
  return ltv;
};

export const getLeaseTermInMonthsForNumberOfPaymentsAndFrequency = (numberOfPayments: number, numberOfPaymentsPerYear: number | undefined): number => {
  const termInMonths = divide(numberOfPayments, divide(numberOfPaymentsPerYear ?? 0, 12), 2);
  return Math.ceil(termInMonths);
}

export const debtToIncome = (debt: number, income: number): number => {
  if (income === 0)
    return 0;
  return Number.parseFloat(Big(debt).div(income).mul(100).toFixed(2));
};

export const paymentToIncome = (payment: number, paymentsPerYear: number | undefined, income: number): number => {
  if (income === 0 || !hasValue(paymentsPerYear)) {
    return 0;
  }

  const convertedMonthlyPaymentAmount = getConvertedMonthlyPaymentAmount(paymentsPerYear!, payment);

  return Number.parseFloat(Big(convertedMonthlyPaymentAmount).div(income).mul(100).toFixed(2));
};

// Takes the number of payments and the payment amount and converts it over to how much the borrower is paying every month
export const getConvertedMonthlyPaymentAmount = (paymentsPerYear: number, payment: number): number =>
  paymentsPerYear === 12
    ? payment
    : divide(multiply(paymentsPerYear ?? 0, payment), 12);

export const paymentToIncomeWithPaymentFrequency = (payment: number, income: number, quotePayTypes: IQuotePayType[], paymentFrequency: number): number => {
  const paymentsInYear = quotePayTypes.find(qpt => qpt.amspayType === paymentFrequency)?.paymentsPerYear;

  if (income === 0 || !paymentsInYear) {
    return 0;
  }

  // Getting the total amount of payment for a year and dividing it by 12 so that it can be used to calculate with monthly income
  const paymentPerMonth = getConvertedMonthlyPaymentAmount(paymentsInYear, payment); //paymentsInYear * payment / 12;

  return Number.parseFloat(Big(paymentPerMonth).div(income).mul(100).toFixed(2));
};

export const getAmortizationSchedule = (quote: IQuote, monthlyDepreciationRate?: number | null, bookValue?: number): AmortizationItem[] => {
  const amortizationTable: AmortizationItem[] = [];
  let balance = Big(quote.amountToFinance ?? 0);
  const apr = Big(quote.apr ?? 0);
  let payment = 0;
  const financeTerm = quote.term + (quote.balloonTerm ?? 0);
  for (let i = 1; i <= financeTerm; i++) {
    const balanceParsed = Number.parseFloat(balance.toFixed(2));
    if (i === 1) {
      payment = calculatePayment(Number.parseFloat(apr.toFixed(20)), 12, financeTerm, balanceParsed);
    }
    const interest = apr.div(100).div(12).mul(balance);
    const interestParsed = Number.parseFloat(interest.toFixed(7));
    const principal = Big(payment).minus(interest);
    const collateralValue =
      i === 1 && bookValue && monthlyDepreciationRate
        ? bookValue * (1 - monthlyDepreciationRate)
        : i !== 1 && bookValue && monthlyDepreciationRate && amortizationTable[i - 2].collateralValue
          ? amortizationTable[i - 2].collateralValue! * (1 - monthlyDepreciationRate)
          : undefined;

    amortizationTable.push({
      beginningBalance: balanceParsed,
      paymentNumber: i,
      payment,
      principal: Number.parseFloat(principal.toFixed(2)),
      interest: interestParsed,
      endingPrincipal: Number.parseFloat(Big(balance).minus(principal).toFixed(2)),
      collateralValue: collateralValue
    });
    balance = balance.minus(principal);
  }
  // console.log('Amortization Table', amortizationTable);
  return amortizationTable;
};

export const calculateBalloonPayment = (quote: IQuote): number | undefined => {
  if (!quote || !quote.term) return undefined;
  const amortizationSchedule = getAmortizationSchedule(quote);
  const balloonPaymentNumber = quote.term - 1; // since 0 indexed a term of 48 would mean 48th payment is in index 47 and 49th(balloon) is in index 49
  if (amortizationSchedule.length <= balloonPaymentNumber + 1) return 0;
  return amortizationSchedule[balloonPaymentNumber].endingPrincipal + amortizationSchedule[balloonPaymentNumber + 1].interest;
};

export const handleChangeSelectedFinanceLoanClass = async (currentApp: IApplication, financeLoanClass: FinanceLoanClass, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine) => {
  // Sees if the loan class has a County Tax fee
  const loanClassCountyTax = financeLoanClass?.financeLoanClassFinanceTaxes.find(tax => tax.description.trim().toUpperCase() === "COUNTY TAX");
  let zipCodeOnQuote: ZipCode | undefined = currentApp.financeQuotes.length > 0
    ? currentApp.financeQuotes[0].zipCode
    : undefined;
  if (!zipCodeOnQuote && shouldFetchZipcode(financeLoanClass)) {
    // Gets the zip code if it's not on the object for some reason
    const zipCodeResponse = await getZipCodeInfo(currentApp, dispatch);

    if (typeof zipCodeResponse !== 'string') {
      zipCodeOnQuote = zipCodeResponse;
    }
  }

  const updatedApp = produce(currentApp!, updatedCurrentApp => {    
    // Applying the same values to each quote
    updatedCurrentApp.financeQuotes.forEach(financeQuote => {
      if (financeQuote) {
        updateFinanceLoanClassValuesOnFinanceQuote(financeLoanClass, financeQuote, zipCodeOnQuote, false);
      }
    });
  });

  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
}

export const handleFinanceQuoteZipCodeUpdate = async (currentApp: IApplication, financeLoanClass: FinanceLoanClass, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine): Promise<void> => {
  if (!shouldFetchZipcode(financeLoanClass)) {
    return;
  }

  const zipCodeResponse = await getZipCodeInfo(currentApp, dispatch);
  let zipCodeInfo: ZipCode | undefined = undefined;
  const zipReceivedSuccessfully = typeof zipCodeResponse !== 'string';

  if (zipReceivedSuccessfully) {
    zipCodeInfo = zipCodeResponse;
  } else {
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: { isError: true, message: zipCodeResponse },
    });
  }

  const updatedApp: IApplication = produce(currentApp, updatedCurrentApp => {
    const financeQuotes = updatedCurrentApp.financeQuotes;

    // Applying the same values to each quote
    financeQuotes.forEach(financeQuote => {
      updateFinanceQuoteZipCode(financeQuote, financeLoanClass, zipCodeInfo);
    });
  });

  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
}

export const getZipCodeForPrimary = (currentApp: IApplication): string | undefined => {
  return currentApp.appCustomers.find(ac => ac.customerRole.isPrimaryApplicant)!.customer.customerAddress.find(ca => ca.default)?.zipCode ?? '';
}

// The zip code fetching should only happen if the loan class has a county tax (subject to changing in the future)
export const shouldFetchZipcode = (financeLoanClass: FinanceLoanClass | undefined): boolean => {
  return financeLoanClass !== undefined && financeLoanClass.financeLoanClassFinanceTaxes.some(tax => tax.description.toUpperCase() === "COUNTY TAX");
}

// export const getFinanceQuoteCountyTaxFee = (currentApp: IApplication, financeLoanClasses: FinanceLoanClass[]): FinanceQuoteFee | undefined => {
//   const financeLoanClass = financeLoanClasses.find(flc => flc.id === currentApp.financeQuote?.financeLoanClassId);
//   const countyTaxFee = financeLoanClass?.financeLoanClassFinanceFees.find(fee => fee.description === "COUNTY TAX");
//   return currentApp.financeQuote?.financeQuoteFees.find(quoteFee => quoteFee.financeLoanClassFinanceFeeId === countyTaxFee?.id);
// }

export const getZipCodeInfo = async (currentApp: IApplication, dispatch: ApplicationDispatch): Promise<ZipCode | string> => {
  try {
    const zipCodeForPrimaryApplicant = getZipCodeForPrimary(currentApp);
    const zipCodeUrl = `api/ZipCode/GetZipCodeByZip/${zipCodeForPrimaryApplicant}`
    const response = await fetchData(zipCodeUrl, dispatch);

    if (response.error) {
      return response.error as string;
    } else {
      return response as ZipCode;
    }
  } catch (err) {
    return err as string;
  }
}

// Used across the multiple places where the financeQuote is updated
export const handleUpdateSingleQuoteField = <K extends keyof FinanceQuote>(
  fieldName: K,
  value: FinanceQuote[K], 
  financeQuoteId: string, 
  currentApp: IApplication, 
  financeLoanClass: FinanceLoanClass,
  financeWarranties: FinanceWarranty[],
  dispatch: ApplicationDispatch, 
  businessRulesEngine: BusinessRulesEngine
) => {
  const updatedFinanceQuotes: ApplicationFinanceQuote[] = currentApp.financeQuotes.map(afq => {
    return { ...afq };
  });

  // Applying the same values to each quote (unless where noted)
  updatedFinanceQuotes.forEach(financeQuoteToUpdate => {
    const updateAllFields = financeQuoteToUpdate.id === financeQuoteId;
    updateSingleFinanceQuoteValue(financeQuoteToUpdate, financeLoanClass, updateAllFields, fieldName, value, false, financeWarranties);
  });

  updateFinanceQuotes(
    currentApp,
    updatedFinanceQuotes,
    dispatch,
    businessRulesEngine);
};

// Sets the selected quote as such and marks the others as not selected
export const handleUpdateSelectedQuote = (selectedFinanceQuoteId: string, currentApp: IApplication, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine) => {
  const updatedFinanceQuotes: ApplicationFinanceQuote[] = currentApp.financeQuotes.map(afq => {
    return { ...afq };
  });

  // Applying the same values to each quote (unless where noted)
  updatedFinanceQuotes.forEach(updatedFinanceQuote => {
    updatedFinanceQuote.isSelected = updatedFinanceQuote.id === selectedFinanceQuoteId;
  });

  updateFinanceQuotes(
    currentApp,
    updatedFinanceQuotes,
    dispatch,
    businessRulesEngine);
};

export const updateFinanceQuotes = (currentApp: IApplication, financeQuotes: ApplicationFinanceQuote[], dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine) => {
  const updatedApp = produce(currentApp, newCurrentApp => {
    newCurrentApp.financeQuotes = financeQuotes;
  });
  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
}

const getFinanceQuoteErrorMessage = (applicationFinanceErrors: IRuleViolation[], displayFieldId: string): string | undefined => {
  return applicationFinanceErrors.find(e => e.displayFieldId === displayFieldId)?.summary;
}

export const getFinanceQuoteErrorMessages = (applicationFinanceErrors: IRuleViolation[]) => {
  return {
    selectedFinanceLoanClass: getFinanceQuoteErrorMessage(applicationFinanceErrors, "selectedFinanceLoanClass"),
    contractDate: getFinanceQuoteErrorMessage(applicationFinanceErrors, "contractDate"),
    firstPaymentDate: getFinanceQuoteErrorMessage(applicationFinanceErrors, "firstPaymentDate"),
    interestStartDate: getFinanceQuoteErrorMessage(applicationFinanceErrors, "interestStartDate"),
    termInMonths: getFinanceQuoteErrorMessage(applicationFinanceErrors, "termInMonths"),
    numberOfPayments: getFinanceQuoteErrorMessage(applicationFinanceErrors, "numberOfPayments"),
    rate: getFinanceQuoteErrorMessage(applicationFinanceErrors, "rate"),
    sellingPrice: getFinanceQuoteErrorMessage(applicationFinanceErrors, "sellingPrice"),
    downPayment: getFinanceQuoteErrorMessage(applicationFinanceErrors, "downPayment"),
    paymentFrequency: getFinanceQuoteErrorMessage(applicationFinanceErrors, "paymentFrequency"),
    deferredDownPaymentPaymentFrequency: getFinanceQuoteErrorMessage(applicationFinanceErrors, "deferredDownPaymentPaymentFrequency"),
    irregularPaymentStreams: getFinanceQuoteErrorMessage(applicationFinanceErrors, "irregularPaymentStreams"),
    totalDeferredDown: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalDeferredDown"),
    totalFees: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalFees"),
    totalTaxes: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalTaxes"),
    totalAddons: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalAddons"),
    tradeInValue: getFinanceQuoteErrorMessage(applicationFinanceErrors, "tradeInValue"),
    tradeInPayoff: getFinanceQuoteErrorMessage(applicationFinanceErrors, "tradeInPayoff"),
    payment: getFinanceQuoteErrorMessage(applicationFinanceErrors, "payment"),
    balloonFinalPayment: getFinanceQuoteErrorMessage(applicationFinanceErrors, "balloonFinalPayment"),
    amountFinanced: getFinanceQuoteErrorMessage(applicationFinanceErrors, "amountFinanced"),
    financeCharge: getFinanceQuoteErrorMessage(applicationFinanceErrors, "financeCharge"),
    totalOfPayments: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalOfPayments"),
    apr: getFinanceQuoteErrorMessage(applicationFinanceErrors, "apr"),
    balloon: getFinanceQuoteErrorMessage(applicationFinanceErrors, "balloon"),
    contractType: getFinanceQuoteErrorMessage(applicationFinanceErrors, "contractType"),
    solveFor: getFinanceQuoteErrorMessage(applicationFinanceErrors, "solveFor"),
    backEndProfit: getFinanceQuoteErrorMessage(applicationFinanceErrors, "backEndProfit"),
    frontEndProfit: getFinanceQuoteErrorMessage(applicationFinanceErrors, "frontEndProfit"),
    bankFee: getFinanceQuoteErrorMessage(applicationFinanceErrors, "bankFee"),
    totalProfit: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalProfit"),
    addons: getFinanceQuoteErrorMessage(applicationFinanceErrors, "addons")
  }
}

export const getLeaseQuoteErrorMessages = (applicationFinanceErrors: IRuleViolation[]) => {
  return {
    selectedFinanceLoanClass: getFinanceQuoteErrorMessage(applicationFinanceErrors, "selectedFinanceLoanClass"),
    contractDate: getFinanceQuoteErrorMessage(applicationFinanceErrors, "contractDate"),
    firstPmtDate: getFinanceQuoteErrorMessage(applicationFinanceErrors, "firstPmtDate"),
    termInMonths: getFinanceQuoteErrorMessage(applicationFinanceErrors, "termInMonths"),
    numberOfPmts: getFinanceQuoteErrorMessage(applicationFinanceErrors, "numberOfPmts"),
    apr: getFinanceQuoteErrorMessage(applicationFinanceErrors, "apr"),
    sellingPrice: getFinanceQuoteErrorMessage(applicationFinanceErrors, "sellingPrice"),
    cashDown: getFinanceQuoteErrorMessage(applicationFinanceErrors, "cashDown"),
    paymentFrequencyId: getFinanceQuoteErrorMessage(applicationFinanceErrors, "paymentFrequencyId"),
    totalDeferredDown: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalDeferredDown"),
    totalFees: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalFees"),
    totalTaxes: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalTaxes"),
    tradeAllowance: getFinanceQuoteErrorMessage(applicationFinanceErrors, "tradeAllowance"),
    tradeInLien: getFinanceQuoteErrorMessage(applicationFinanceErrors, "tradeInLien"),
    fullPaymentAmount: getFinanceQuoteErrorMessage(applicationFinanceErrors, "fullPaymentAmount"),
    financeCharge: getFinanceQuoteErrorMessage(applicationFinanceErrors, "financeCharge"),
    totalOfPayments: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalOfPayments"),
    moneyFactor: getFinanceQuoteErrorMessage(applicationFinanceErrors, "moneyFactor"),
    capCost: getFinanceQuoteErrorMessage(applicationFinanceErrors, "capCost"),
    totalDepreciation: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalDepreciation"),
    residualValue: getFinanceQuoteErrorMessage(applicationFinanceErrors, "residualValue"),
    totalDueAtSigningCash: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalDueAtSigningCash"),
    taxPortionOfDownPayment: getFinanceQuoteErrorMessage(applicationFinanceErrors, "taxPortionOfDownPayment"),
    totalDownRollback: getFinanceQuoteErrorMessage(applicationFinanceErrors, "totalDownRollback"),
    mileage: getFinanceQuoteErrorMessage(applicationFinanceErrors, "mileage"),
    securityDeposit: getFinanceQuoteErrorMessage(applicationFinanceErrors, "securityDeposit"),
    discountPercent: getFinanceQuoteErrorMessage(applicationFinanceErrors, "discountPercent"),
    residualPercent: getFinanceQuoteErrorMessage(applicationFinanceErrors, "residualPercent"),
    leaseLoanClassId: getFinanceQuoteErrorMessage(applicationFinanceErrors, "leaseLoanClassId"),
    fees: getFinanceQuoteErrorMessage(applicationFinanceErrors, "fees"),
    taxes: getFinanceQuoteErrorMessage(applicationFinanceErrors, "taxes"),
    tradeInVehicles: getFinanceQuoteErrorMessage(applicationFinanceErrors, "tradeInVehicles"),
  }
}

// Looks at the configuration item list of disabled fields and also the solveFor option to determine if the field is disabled
export const financeQuoteFieldIsDisabled = (disabledFields: string[], fieldName: string, solveFor: number): boolean => {
  switch (solveFor) {
    case 1: // Cash
      if (fieldName === "sellingPrice") {
        return true;
      }
      break;
    case 2: // Payment
      if (fieldName === "payment") {
        return true;
      }
      break;
    case 4: // Term
      if (["numberOfPayments", "termInMonths"].includes(fieldName)) {
        return true;
      }
      break;
    case 5: // Rate
      if (fieldName === "rate") {
        return true;
      }
      break;
    case 999: // DownPayment
      if (fieldName === "downPayment") {
        return true;
      }
      break;
  }

  return disabledFields.includes(fieldName);
}

export const leaseQuoteFieldIsDisabled = (disabledFields: string[], fieldName: string): boolean => disabledFields.includes(fieldName);

export const getBackgroundColorForPotentialSolveForFields = (fieldName: string, solveFor: number): CSSProperties | undefined => {
  const lightGreenBackgroundStyle: CSSProperties = { backgroundColor: "#DAFFDD" };

  switch (solveFor) {
    case 1: // Cash
      if (fieldName === "sellingPrice") {
        return lightGreenBackgroundStyle;
      }
      break;
    case 2: // Payment
      if (fieldName === "payment") {
        return lightGreenBackgroundStyle;
      }
      break;
    case 4: // Term
      if (["numberOfPayments", "termInMonths"].includes(fieldName)) {
        return lightGreenBackgroundStyle;
      }
      break;
    case 5: // Rate
      if (fieldName === "rate") {
        return lightGreenBackgroundStyle;
      }
      break;
    case 999: // DownPayment
      if (fieldName === "downPayment") {
        return lightGreenBackgroundStyle;
      }
      break;
  }

  return undefined;
}

export const getValidSolveForOptionsForContractType = (contractType: CarletonItem | undefined, solveForOptionsForClient: CarletonItem[]): CarletonItem[] => {
  if (!contractType || solveForOptionsForClient.length === 0) {
    return [];
  }

  let validOptionNamesForContractType: string[] = [];

  // Taken from Appendix 1: Calculation Requirements in the CarletonCalcs documentation PDF
  switch (contractType?.option) {
    case "Installment":
      validOptionNamesForContractType = ["Cash", "Payment", "Term", "Rate", "DownPayment"];
      break;
    case "Balloon":
      validOptionNamesForContractType = ["Cash", "Payment", "Balloon", "Term", "Rate"];
      break;
    case "SinglePayment":
      validOptionNamesForContractType = ["Cash", "Payment"];
      break;
    case "InterestOnly":
      validOptionNamesForContractType = ["Cash", "Payment", "Term", "Rate"];
      break;
    default:
      return [];
  }

  const validSolveForOptionsForContractType: CarletonItem[] = solveForOptionsForClient.filter(option => validOptionNamesForContractType.includes(option.option));
  return validSolveForOptionsForContractType;
}

export const getLeaseNumberOfPaymentsForTermInMonthsAndFrequency = (termInMonths: number | undefined, paymentFrequency: number | undefined): number => {
  if (termInMonths === undefined || paymentFrequency === undefined) {
    return 0;
  }

  const numberOfPaymentsPerYear = getNumberOfPaymentsPerYearForCarletonPaymentFrequency(paymentFrequency);
  return getNumberOfPaymentsForTermInMonthsAndNumberOfPaymentsPerYear(termInMonths, paymentFrequency);
}

/**
 * Estimates the number of payments when solving for term.
 * Uses the inverse of the standard amortization formula.
 *
 * Formula: n = -log(1 - (Principal × r / Payment)) / log(1 + r)
 *
 * @param payment - The payment amount
 * @param amountFinanced - The principal/amount financed
 * @param apr - Annual percentage rate (e.g., 5.5 for 5.5%)
 * @param paymentsPerYear - Number of payments per year (12 for monthly, 26 for bi-weekly, etc.)
 * @returns Estimated number of payments, or undefined if calculation is invalid
 */
export const estimateNumberOfPayments = (
  payment: number,
  amountFinanced: number,
  apr: number,
  paymentsPerYear: number
): number | undefined => {
  if (payment <= 0 || amountFinanced <= 0 || paymentsPerYear <= 0) {
    return undefined;
  }

  // Handle 0% APR case - simple division
  if (apr === 0) {
    return Math.ceil(amountFinanced / payment);
  }

  const periodicRate = apr / 100 / paymentsPerYear;

  // Check if payment is sufficient to cover interest
  const interestOnly = amountFinanced * periodicRate;
  if (payment <= interestOnly) {
    return undefined; // Payment too small - loan would never be paid off
  }

  // n = -log(1 - (Principal × r / Payment)) / log(1 + r)
  const numerator = 1 - (amountFinanced * periodicRate / payment);

  if (numerator <= 0) {
    return undefined; // Invalid - payment doesn't cover interest
  }

  const numberOfPayments = -Math.log(numerator) / Math.log(1 + periodicRate);

  return Math.ceil(numberOfPayments); // Round up to whole payments
};

/**
 * Converts estimated number of payments back to term in months
 *
 * @param numberOfPayments - The number of payments
 * @param paymentsPerYear - Number of payments per year
 * @returns Term in months (rounded up)
 */
export const estimateTermInMonthsFromPayments = (
  numberOfPayments: number,
  paymentsPerYear: number
): number => {
  if (paymentsPerYear <= 0) {
    return 0;
  }
  return Math.ceil((numberOfPayments * 12) / paymentsPerYear);
};

/**
 * Estimates the number of payments and term in months when solving for term.
 * Convenience function that combines estimateNumberOfPayments and estimateTermInMonthsFromPayments.
 *
 * @param payment - The payment amount
 * @param amountFinanced - The principal/amount financed
 * @param interestRate - Annual percentage rate (e.g., 5.5 for 5.5%)
 * @param paymentFrequency - Carleton PaymentFrequency enum value
 * @returns Object with estimated numberOfPayments and termInMonths, or undefined values if invalid
 */
export const estimateTermFromPayment = (
  payment: number,
  amountFinanced: number,
  interestRate: number,
  paymentFrequency: PaymentFrequency
): { numberOfPayments: number | undefined; termInMonths: number | undefined } => {
  const paymentsPerYear = getNumberOfPaymentsPerYearForCarletonPaymentFrequency(paymentFrequency);
  const numberOfPayments = estimateNumberOfPayments(payment, amountFinanced, interestRate, paymentsPerYear);

  if (numberOfPayments === undefined) {
    return { numberOfPayments: undefined, termInMonths: undefined };
  }

  const termInMonths = estimateTermInMonthsFromPayments(numberOfPayments, paymentsPerYear);
  return { numberOfPayments, termInMonths };
};

export const getQuoteBackgroundColor = (isQuoteDirty?: boolean): string => {
  return isQuoteDirty ? "lightyellow" : "white";
}

// Used By ACC only for the Lease Quote
export const isArkansasResidentBuyingOutsideArkansas = (currentApp: IApplication, organizations: Organization[], states: IState[]): boolean => {
  const arkansasStateId = states.find(s => s.abbreviation === 'AR')?.id;
  const primaryApplicantCurrentAddress = getApplicant(currentApp, Constants.roleApplicantId, true)?.customer.customerAddress.find(ca => ca.default);
  const isArkansasResident = primaryApplicantCurrentAddress?.stateId.toLowerCase() === arkansasStateId;
  const iaApplicationLocationOutsideArkansas = organizations.find(o => o.id === currentApp.organizationId)?.stateAbbreviation !== 'AR';

  return isArkansasResident && iaApplicationLocationOutsideArkansas;
}

// Used By ACC only for the Lease Quote
export const getSellingPriceForLeaseQuote = (currentApp: IApplication, organizations: Organization[], states: IState[]): number => {
  const vehiclePriceOnApp = (currentApp.vehicle?.carPrice ?? 0)
  const specialArkansasSellingPrice = 600 + vehiclePriceOnApp; // For an Arkansas resident buying outside of Arkansas

  return isArkansasResidentBuyingOutsideArkansas(currentApp, organizations, states)
    ? specialArkansasSellingPrice
    : vehiclePriceOnApp;
}

// Returns the array of finance fees that are not supposed to be hidden
export const getFinanceQuoteFeesThatAreDisplayed = (
  quoteFees: FinanceQuoteFee[],
  financeLoanClassFinanceFees: FinanceLoanClassFinanceFee[] | undefined
): FinanceQuoteFeeWithDescription[] => {
  const financeFeeIdsToHideFromFeeTab = financeLoanClassFinanceFees
    ?.filter(ff => ff.hideFromFeeTab || hasValue(ff.carletonInsuranceCoverageTypeId))
    .map(ff => ff.id);
  
  const appQuoteFeesToShow = financeFeeIdsToHideFromFeeTab
    ? quoteFees.filter(qf => !financeFeeIdsToHideFromFeeTab.includes(qf.financeLoanClassFinanceFeeId))
    : quoteFees;

  const financeFeesToShowWithDescription: FinanceQuoteFeeWithDescription[] = appQuoteFeesToShow.map(aqf => {
    const financeFeeDescriptionForAppQuoteFee = financeLoanClassFinanceFees?.find(flcff => flcff.id === aqf.financeLoanClassFinanceFeeId)?.description ?? "";

    return {
      ...aqf,
      description: financeFeeDescriptionForAppQuoteFee
    }
  });

  return financeFeesToShowWithDescription;
}

// Returns the array of finance fees that are do not have the string "COST" in the description
export const getFinanceQuoteFeesThatAreNotCost = (quoteFees: FinanceQuoteFee[], financeFees: FinanceLoanClassFinanceFee[] | undefined): FinanceQuoteFee[] => {
  const financeFeesThatAreNotCost = financeFees?.filter(ff => ff.description.toUpperCase().includes("COST")).map(ff => ff.id);
  return financeFeesThatAreNotCost
    ? quoteFees.filter(qf => !financeFeesThatAreNotCost.includes(qf.financeLoanClassFinanceFeeId))
    : quoteFees;
}

export const isApplicationBeforeOldQuoterCutoff = (applicationDate: Date | undefined, financeQuoterCutoverDate: Date | undefined): boolean => {
  return hasValue(financeQuoterCutoverDate)
    && hasValue(applicationDate)
    && new Date(financeQuoterCutoverDate!).getTime() > new Date(applicationDate!).getTime()
}

// Returns the selected, non-deleted quote from a list of finance quotes.
const findSelectedFinanceQuote = (financeQuotes: ApplicationFinanceQuote[] | undefined): ApplicationFinanceQuote | undefined => {
  return financeQuotes?.find(afq => !hasValue(afq.deletedDate) && afq.isSelected);
}

export const getSelectedFinanceQuote = (currentApp: IApplication | undefined): ApplicationFinanceQuote | undefined => {
  return findSelectedFinanceQuote(currentApp?.financeQuotes);
}

// Returns a copy of the selected quote out of a freshly loaded or freshly solved set of finance quotes.
// Held in state as lastSolvedSelectedFinanceQuote so down payment, balance, and posting checks read what
// was actually solved instead of unsolved edits sitting on the selected quote.
export const buildLastSolvedSelectedFinanceQuote = (financeQuotes: ApplicationFinanceQuote[] | undefined): ApplicationFinanceQuote | undefined => {
  const selected = findSelectedFinanceQuote(financeQuotes);
  return selected ? { ...selected } : undefined;
}

// Make sure the leaseFees array is already filtered for the loan class
export const getLeaseFeeByDescription = (leaseFees: ILeaseFee[], leaseFeeDescription: string): ILeaseFee | undefined => {
  return leaseFees?.find(lf => lf.description.trim() === leaseFeeDescription.trim());
}

// Returns the value for the given list of custom fields by matching the value of the customerCreditOverrideField
export const getCustomFieldValue = (customFields: IEntityCustomField[], customerCreditOverrideField: string): string | undefined => {
  const customField = customFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === customerCreditOverrideField.toLowerCase());
  return customField?.value;
}

/** The lease quote fields the amount due at signing reads. */
export type LeaseAmountDueQuote = Pick<IApplicationLeaseQuote,
  'totalDueAtSigningCash' | 'deferredPmtAmount1' | 'deferredPmtAmount2' | 'deferredPmtAmount3'
  | 'deferredPmtAmount4' | 'deferredPmtAmount5' | 'deferredPmtAmount6'>;

const leaseDeferredDown = (quote: LeaseAmountDueQuote): Big =>
  [quote.deferredPmtAmount1, quote.deferredPmtAmount2, quote.deferredPmtAmount3,
    quote.deferredPmtAmount4, quote.deferredPmtAmount5, quote.deferredPmtAmount6]
    .reduce<Big>((sum, amount) => sum.plus(amount ?? 0), new Big(0));

/**
 * What a lease customer pays at signing, as AMS 2000's lease screen collects it (LOS card down payments B1): Total Down
 * as DB Sync worked it out (`totalDueAtSigningCash`) less the deferred down payments, which are collected later and post
 * on their own, rounded to the cent (half away from zero). Undefined until the quote is solved; below 0 when the
 * deferred payments exceed Total Down. The server's twin is `ApplicationLeaseQuote.AmountDueAtSigning()`.
 */
export const getLeaseAmountDueAtSigning = (quote: LeaseAmountDueQuote | null | undefined): number | undefined => {
  const totalDown = quote?.totalDueAtSigningCash;
  if (quote === null || quote === undefined || totalDown === null || totalDown === undefined) {
    return undefined;
  }
  return new Big(totalDown).minus(leaseDeferredDown(quote)).round(2, Big.roundHalfUp).toNumber();
}

/**
 * Why a lease can't take or post its down payment yet (B1, B3): not solved, or the deferred down payments are more than
 * Total Down. Undefined when the amount due at signing can be used.
 */
export const getLeaseAmountDueRefusal = (quote: LeaseAmountDueQuote | null | undefined): string | undefined => {
  const due = getLeaseAmountDueAtSigning(quote);
  if (due === undefined) {
    return 'Solve the lease quote first.';
  }
  if (due < 0) {
    return `The deferred down payments (${toDollars(leaseDeferredDown(quote!).toNumber())}) are more than Total Down (${toDollars(quote!.totalDueAtSigningCash ?? 0)}). Adjust the lease quote.`;
  }
  return undefined;
}

// Returns the down payment required by the quote for the given application.
// For finance-like types (Finance, Refinance, Side Note; see usesFinanceQuote), this is downPayment from the last
// solved selected finance quote, so the value always matches what the customer was last quoted rather than unsolved
// edits sitting on the selected quote.
// For Lease, this is the amount due at signing (getLeaseAmountDueAtSigning: Total Down less deferred down), and
// undefined until the lease quote is solved; callers show getLeaseAmountDueRefusal instead of treating it as $0.
// For Indirect, this is cashDown from the underwriter quote, unless the deal has been moved forward to a
// finance quote (see below), in which case it reads the finance quote like a direct Finance deal.
// For undefined quote types, returns 0.
export const getDownPaymentFromQuote = (
  currentApp: IApplication | undefined,
  lastSolvedSelectedFinanceQuote?: ApplicationFinanceQuote,
  configuration?: IConfiguration
): number | undefined => {
  if (usesFinanceQuote(currentApp?.quoteType)) {
    return lastSolvedSelectedFinanceQuote?.downPayment ?? 0;
  }

  switch (currentApp?.quoteType) {
    case ApplicationQuoteType.Lease:
      return getLeaseAmountDueAtSigning(currentApp?.applicationLeaseQuote);

    case ApplicationQuoteType.Indirect:
      if (isIndirectConvertedToFinance(currentApp, configuration)) {
        return lastSolvedSelectedFinanceQuote?.downPayment ?? 0;
      }

      const underwriterAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter);
      return underwriterAppQuote?.quote.cashDown ?? 0;

    default:
      return 0;
  }
}

// Returns the balance on the quote for the given application.
// For finance-like types (Finance, Refinance, Side Note; see usesFinanceQuote), this is amountFinanced from the last
// solved selected finance quote.
// For Lease, this is capCost.
// For Indirect, this is disclosureAmountFinanced from the application funding, unless the deal has been moved
// forward to a finance quote (see below), in which case it reads the finance quote like a direct Finance deal.
// For undefined quote types, returns 0.
//
// The indirect split mirrors AmsLoanService.GetLoanPostingSource() on the server, which posts an indirect deal
// from the selected finance quote when the client prints its loan documents in the LOS. Reading the funding
// record here while the API posts and validates the finance quote would let this UI reject a valid balance or
// show one the API never uses.
export const getBalanceOnQuote = (
  currentApp: IApplication | undefined,
  lastSolvedSelectedFinanceQuote?: ApplicationFinanceQuote,
  configuration?: IConfiguration
): number => {
  if (usesFinanceQuote(currentApp?.quoteType)) {
    return lastSolvedSelectedFinanceQuote?.amountFinanced ?? 0;
  }

  switch (currentApp?.quoteType) {
    case ApplicationQuoteType.Lease:
      return currentApp?.applicationLeaseQuote?.capCost ?? 0;
    case ApplicationQuoteType.Indirect:
      // Until the underwriter quote is actually moved forward there is no finance quote to read, so such a deal
      // keeps reading ApplicationFunding even with the flag on.
      return isIndirectConvertedToFinance(currentApp, configuration)
        ? lastSolvedSelectedFinanceQuote?.amountFinanced ?? 0
        : currentApp?.applicationFunding?.disclosureAmountFinanced ?? 0;
    default:
      return 0;
  }
}

export interface IrregularPaymentStartDateOption {
  date: Date;
  label: string;
}

/**
 * Advances a date by a specified number of payment periods based on the payment frequency.
 * @param startDate The starting date to advance from
 * @param paymentFrequency The payment frequency enum value
 * @param periods The number of periods to advance (default 1)
 * @returns A new Date advanced by the specified number of periods
 */
export function advanceDateByPaymentPeriods(
  startDate: Date,
  paymentFrequency: PaymentFrequency,
  periods: number = 1
): Date {
  const resultDate = new Date(startDate);

  switch (paymentFrequency) {
    case PaymentFrequency.Weekly:
      resultDate.setDate(resultDate.getDate() + (periods * 7));
      break;
    case PaymentFrequency.Biweekly:
      resultDate.setDate(resultDate.getDate() + (periods * 14));
      break;
    case PaymentFrequency.Semimonthly:
      // Semimonthly: typically two fixed days per month (e.g., 1st and 15th).
      // If the start date is on a standard semimonthly anchor (1st or 15th),
      // advance using calendar-based semimonthly positions. Otherwise, fall back
      // to the previous behavior to avoid unexpected changes.
      if (periods === 0) {
        // No advancement; keep the original date.
        break;
      }

      const startDayOfMonth = resultDate.getDate();

      if (startDayOfMonth === 1 || startDayOfMonth === 15) {
        // Use fixed anchors: 1st (index 0) and 15th (index 1) of each month.
        const startIndexInMonth = startDayOfMonth === 1 ? 0 : 1;
        const overallIndex = startIndexInMonth + periods;
        const monthOffset = Math.floor(overallIndex / 2);
        const positionInMonth = overallIndex % 2; // 0 -> 1st, 1 -> 15th

        resultDate.setMonth(resultDate.getMonth() + monthOffset);
        resultDate.setDate(positionInMonth === 0 ? 1 : 15);
      } else {
        // Fallback to prior logic for non-standard start days to preserve behavior.
        resultDate.setMonth(resultDate.getMonth() + Math.floor(periods / 2));
        if (periods % 2 === 1) {
          resultDate.setDate(resultDate.getDate() + 15);
        }
      }
      break;
    case PaymentFrequency.FourWeekly:
      resultDate.setDate(resultDate.getDate() + (periods * 28));
      break;
    case PaymentFrequency.Monthly:
      resultDate.setMonth(resultDate.getMonth() + periods);
      break;
    case PaymentFrequency.Bimonthly:
      resultDate.setMonth(resultDate.getMonth() + (periods * 2));
      break;
    case PaymentFrequency.Quarterly:
      resultDate.setMonth(resultDate.getMonth() + (periods * 3));
      break;
    case PaymentFrequency.FourMonthly:
      resultDate.setMonth(resultDate.getMonth() + (periods * 4));
      break;
    case PaymentFrequency.Semiannual:
      resultDate.setMonth(resultDate.getMonth() + (periods * 6));
      break;
    case PaymentFrequency.Annual:
      resultDate.setFullYear(resultDate.getFullYear() + periods);
      break;
    default:
      resultDate.setMonth(resultDate.getMonth() + periods);
      break;
  }

  return resultDate;
}

/**
 * Calculates possible payment dates based on the first payment date and payment frequency.
 * @param firstPaymentDate The first payment date from the finance quote
 * @param paymentFrequency The payment frequency enum value
 * @param term The number of payment dates to generate (default 60)
 * @returns An array of PaymentDateOption objects with date and formatted label
 */
export function calculatePossiblePaymentDates(
  firstPaymentDate: Date | undefined,
  paymentFrequency: PaymentFrequency | undefined,
  term: number = 60
): IrregularPaymentStartDateOption[] {
  if (!firstPaymentDate || paymentFrequency === undefined) {
    return [];
  }

  return calculatePossibleDatesBasedOnNumberOfDates(firstPaymentDate, paymentFrequency, term);
}

function calculatePossibleDatesBasedOnNumberOfDates(
  startDate: Date, 
  paymentFrequency: PaymentFrequency, 
  numberOfDates: number
): IrregularPaymentStartDateOption[] {
  const dates: IrregularPaymentStartDateOption[] = [];
  for (let i = 0; i < numberOfDates; i++) {
    const paymentDate = advanceDateByPaymentPeriods(startDate, paymentFrequency, i);

    dates.push({
      date: paymentDate,
      label: paymentDate.toLocaleDateString('en-US')
    });
  }

  return dates;
}

// Check for too many payment streams (AMS 2000 can only handle 8)
export const maxPaymentStreams = 8;

export const getDefaultFinanceQuotes = async (
  currentApp: IApplication,
  configuration: IConfiguration,
  activeUser: IEmployee,
  loanClasses: FinanceLoanClass[],
  dispatch: ApplicationDispatch
): Promise<ApplicationFinanceQuote[]> => {
  const loanClassFromQuickQuote = currentApp.contactOpportunityFinanceQuote
    ? loanClasses.find(lc => lc.id.toUpperCase() === currentApp.contactOpportunityFinanceQuote?.financeLoanClassId.toUpperCase())
    : undefined;
  const defaultLoanClassForClient = loanClasses.find(lc => lc.id.toUpperCase() === configuration.defaultAMSLoanClassId?.toUpperCase());
  const loanClass = loanClasses.length > 0
    ? loanClassFromQuickQuote ?? defaultLoanClassForClient ?? loanClasses[0]
    : undefined;
  const primaryApplicantIncomeAndDebt = currentApp.appCustomers.find(x => x.customerRole.isPrimaryApplicant)?.customer.customerIncomeDebt[0];

  let zipCodeInfo: ZipCode | undefined = undefined;
  if (shouldFetchZipcode(loanClass)) {
    const zipCodeResponse = await getZipCodeInfo(currentApp, dispatch);
    if (typeof zipCodeResponse !== 'string') {
      zipCodeInfo = zipCodeResponse;
    }
  }

  const shouldUseCrmQuote = currentApp.contactOpportunityFinanceQuote != null;
  
  const downPaymentLadder: number[] = [];
  for (let i = 0; i < configuration.numberOfApplicationFinanceQuotes; i++) {
    const amountToAddToBase = i * 500; // Adds 500, 1000, 1500, 2000 depending on how many quotes they have
    // Use the down payment from the CRM quote if it exists, otherwise fall back to the down payment from the primary applicant's income and debt (which is where it came from in the old quoter), and if that doesn't exist then default to 0
    const downPaymentForQuote = (currentApp.contactOpportunityFinanceQuote?.downPayment ?? primaryApplicantIncomeAndDebt?.downpayment ?? 0) + amountToAddToBase;
    downPaymentLadder.push(downPaymentForQuote);
  }

  const tieDeferredDown = !configuration.featureFlags[FeatureFlagIds.DontTieFinanceQuoteDeferredDownPaymentsToFirstPaymentDate];
  const customerIds = currentApp.appCustomers.map(ac => ac.customerId);

  const defaultFinanceQuotes: ApplicationFinanceQuote[] = [];
  for (let i = 0; i < downPaymentLadder.length; i++) {
    const sequence = i + 1;
    const isSelected = i === 0; // Only the first quote in the ladder is selected by default

    // Combine both trade-in vehicles into a single trade-in with summed values, excluding soft-deleted ones
    const tradeIn1 = currentApp?.tradeInVehicle && !currentApp.tradeInVehicle.deletedDate ? currentApp.tradeInVehicle : undefined;
    const tradeIn2 = currentApp?.tradeInVehicle2 && !currentApp.tradeInVehicle2.deletedDate ? currentApp.tradeInVehicle2 : undefined;
    const combinedTradeIn: TradeInVehicle | undefined = tradeIn1
      ? {
          ...tradeIn1,
          tradeValue: (tradeIn1.tradeValue ?? 0) + (tradeIn2?.tradeValue ?? 0),
          tradeBalance: (tradeIn1.tradeBalance ?? 0) + (tradeIn2?.tradeBalance ?? 0),
        }
      : tradeIn2;

    // It uses the quick quote from the CRM if it exists and converts it to an ApplicationFinanceQuote
    const financeQuote = shouldUseCrmQuote
      ? convertContactOpportunityQuoteToApplicationFinanceQuote(
          currentApp.contactOpportunityFinanceQuote!,
          loanClass,
          isSelected,
          currentApp?.vehicle,
          combinedTradeIn,
          downPaymentLadder[i],
          zipCodeInfo,
          tieDeferredDown,
          activeUser.id,
          customerIds,
          sequence
        )
      : buildApplicationFinanceQuote(
          loanClass,
          isSelected,
          currentApp?.vehicle,
          combinedTradeIn,
          downPaymentLadder[i],
          zipCodeInfo,
          tieDeferredDown,
          activeUser.id,
          customerIds,
          sequence
        );
    defaultFinanceQuotes.push(financeQuote);
  }

  return defaultFinanceQuotes;
}